import {
  SELECTED_AUDIO_INPUT_KEY,
  SELECTED_VIDEO_INPUT_KEY,
} from "config/livekit";
import { useCallback, useState, useEffect, useRef } from "react";
import { createLocalTracks, VideoPresets, TrackEvent } from "livekit-client";
import useDevices from "../../useDevices/useDevices";
import { useAppStateContext } from "../../../useAppState/useAppState";

export default function useLocalTracks() {
  const MAX_FRAME_RATE = 20;
  const videoResolution = VideoPresets.h360.resolution; // For testing, so we don't use more bandwidth
  const [audioTrack, setAudioTrack] = useState();
  const [videoTrack, setVideoTrack] = useState();
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
  const [, setAcquisitionTrigger] = useState(0);
  const isMountedRef = useRef(true);
  // Use ref for acquisition guard to avoid infinite re-render loop
  const isAcquiringRef = useRef(false);
  const hasAttemptedAcquisition = useRef(false);
  const {
    audioInputDevices,
    videoInputDevices,
    hasAudioInputDevices,
    hasVideoInputDevices,
  } = useDevices();
  const { isSpectator, isRecordSessions } = useAppStateContext();

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!videoTrack) return;

    const handleMuteChanged = () => {
      setIsVideoMuted(videoTrack.isMuted);
    };

    videoTrack.on(TrackEvent.Muted, handleMuteChanged);
    videoTrack.on(TrackEvent.Unmuted, handleMuteChanged);

    setIsVideoMuted(videoTrack.isMuted);

    return () => {
      videoTrack.off(TrackEvent.Muted, handleMuteChanged);
      videoTrack.off(TrackEvent.Unmuted, handleMuteChanged);
    };
  }, [videoTrack]);

  // Clean up tracks when the underlying MediaStreamTrack ends (e.g. permission revoked)
  useEffect(() => {
    if (!audioTrack) return;
    const msTrack = audioTrack.mediaStreamTrack;
    if (!msTrack) return;
    const handleEnded = () => {
      setAudioTrack(undefined);
      hasAttemptedAcquisition.current = false;
      setAcquisitionTrigger(prev => prev + 1);
    };
    msTrack.addEventListener('ended', handleEnded);
    return () => msTrack.removeEventListener('ended', handleEnded);
  }, [audioTrack]);

  useEffect(() => {
    if (!videoTrack) return;
    const msTrack = videoTrack.mediaStreamTrack;
    if (!msTrack) return;
    const handleEnded = () => {
      setVideoTrack(undefined);
      hasAttemptedAcquisition.current = false;
      setAcquisitionTrigger(prev => prev + 1);
    };
    msTrack.addEventListener('ended', handleEnded);
    return () => msTrack.removeEventListener('ended', handleEnded);
  }, [videoTrack]);

  // Re-attempt acquisition when devices update after a failed attempt (e.g. permission granted).
  // We watch audioInputDevices (not just hasAudioInputDevices) because Chrome returns
  // placeholder devices even when denied — the array reference changes when real devices appear.
  useEffect(() => {
    if (hasAudioInputDevices && !audioTrack && !isAcquiringRef.current) {
      hasAttemptedAcquisition.current = false;
      setAcquisitionTrigger(prev => prev + 1);
    }
  }, [hasAudioInputDevices, audioTrack, audioInputDevices]);

  const getAudioAndVideoTracks = useCallback(() => {
    // Use refs for guards to prevent infinite re-render loops
    if (isSpectator) return Promise.resolve();
    if (isAcquiringRef.current || hasAttemptedAcquisition.current) return Promise.resolve();
    if (audioTrack || videoTrack) return Promise.resolve();

    isAcquiringRef.current = true;
    hasAttemptedAcquisition.current = true;
    setIsAcquiringLocalTracks(true);

    const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);
    const hasSelectedAudioDevice = audioInputDevices.some(
      (device) => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId
    );
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);
    const hasSelectedVideoDevice = videoInputDevices.some(
      (device) => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );
    const localTrackConstraints = {
      video: isRecordSessions && hasVideoInputDevices && {
        name: `video-${Date.now()}`,
        resolution: videoResolution,
        frameRate: MAX_FRAME_RATE,
        ...(hasSelectedVideoDevice && {
          deviceId: { exact: selectedVideoDeviceId },
        }),
      },
      // Requested unconditionally: gating on enumeration deadlocks WebKit, which
      // reports no audio inputs until permission is granted, and the prompt only
      // appears once getUserMedia is called. Rejections are handled by the caller
      // in useLiveKitProgramableVideo, which surfaces the NoDeviceFound screen.
      audio: {
        name: `audio-${Date.now()}`,
        echoCancellation: true,
        // Left off: the agent-side voice isolation filter (server, OKO2-3898) needs
        // raw audio. Stacking browser-level noise suppression on top of it is the
        // exact combination LiveKit's docs warn against.
        noiseSuppression: false,
        autoGainControl: true,
        ...(hasSelectedAudioDevice && {
          deviceId: { exact: selectedAudioDeviceId },
        }),
      },
    };

    return createLocalTracks(localTrackConstraints)
      .then((tracks) => {
        // Prevent state updates if component unmounted
        if (!isMountedRef.current) return;

        const videoTrack = tracks.find((track) => track.kind === "video");
        const audioTrack = tracks.find((track) => track.kind === "audio");
        if (videoTrack) {
          setVideoTrack(videoTrack);
        }
        if (audioTrack) {
          setAudioTrack(audioTrack);
        }
      })
      .finally(() => {
        isAcquiringRef.current = false;
        if (isMountedRef.current) {
          setIsAcquiringLocalTracks(false);
        }
      });
  }, [
    isSpectator,
    hasVideoInputDevices,
    audioTrack,
    videoTrack,
    audioInputDevices,
    videoInputDevices,
    isRecordSessions,
    videoResolution,
  ]);

  // Allows external callers to reset the guard and force re-acquisition
  const retryTrackAcquisition = useCallback(() => {
    hasAttemptedAcquisition.current = false;
    setAcquisitionTrigger(prev => prev + 1);
  }, []);

  const localTracks = [audioTrack, videoTrack].filter(
    (track) => track !== undefined
  );

  return {
    localTracks,
    isAcquiringLocalTracks,
    getAudioAndVideoTracks,
    retryTrackAcquisition,
    isVideoMuted,
  };
}
